import React, { useContext, useEffect, useMemo, useState } from "react";
import { Card, Form } from "react-bootstrap";
import SmuBadge from "../../utils/smu-badge/SmuBadge";
import SmuSearch from "../../utils/smu-search/SmuSearch";
import { COLOR_PRIMARY } from "../../constants/colors";
import { prepareText, pascalCase, resolveUserTienda } from "../../helpers";
import SmuTable from "../../utils/smu-table/SmuTable";
import { useNavigate, useLocation } from "react-router-dom";
import { SMU_PRESERVE_PROMOTION_FILTERS } from "../../constants";
import { StoreContext } from "../../store/StoreContext";
import { downloadFaldonXlsx, downloadFaldonXlsxWithCopies, getLatestDownloadFaldonStatus } from "../../api/files";
import { toast } from "../../helpers/smu-alerts/smu-lerts";
import { IconArrowLeft } from "../../icons/Icon";

const ProductsTable = ({ products , faldonId, loading, filter = null, toggleSwitch = false, onToggleSwitchChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedFaldones, setSelectedFaldones] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const { loggedUser, isAdmin } = useContext(StoreContext);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const [cantidadCopias, setCantidadCopias] = useState(1);
  // Ensure downstream logic always works with an array
  const safeProducts = useMemo(() => Array.isArray(products) ? products : [], [products]);

  const handleRowSelect = (row, isSelected) => {
    const faldonName = row.faldon;
    if (isSelected) {
      setSelectedFaldones(prev => (prev.includes(faldonName) ? prev : [...prev, faldonName]));
    } else {
      setSelectedFaldones(prev => prev.filter(name => name !== faldonName));
    }
  };

  const handleSelectAll = () => {
    const uniqueFaldones = Array.from(new Set(filteredProducts.map(p => p.faldon)));
    const allSelected = uniqueFaldones.every(f => selectedFaldones.includes(f));
    if (allSelected) {
      setSelectedFaldones(prev => prev.filter(f => !uniqueFaldones.includes(f)));
    } else {
      setSelectedFaldones(prev => {
        const set = new Set(prev);
        uniqueFaldones.forEach(f => set.add(f));
        return Array.from(set);
      });
    }
  };

  const handleDownloadSelected = async () => {
    const username = loggedUser?.username;
    const tienda = filter?.tienda ? filter?.tienda : resolveUserTienda(isAdmin, loggedUser);
    const selectedProductIds = safeProducts.filter(p => selectedFaldones.includes(p.faldon)).map(p => p.id);
    setDownloading(true);
    setLoadingPercent(0);

    const statusToMax = {FILTERING: 40, RPA_VIRAL: 94, SUCCESS: 100, FAILURE: 100};
    const statusBase = { FILTERING: 1, RPA_VIRAL: 41, SUCCESS: 95, FAILURE: 95 };

    // Progress increment
    let progress = 0;
    let maxDuringDownload = statusToMax.FILTERING;
    const intervalDelay = 150;
    let randomDelayGivenStatus = 150;
    let progressInterval;
    const runProgressCycle = () => {
      randomDelayGivenStatus = progress < statusBase.RPA_VIRAL
        ? Math.floor(Math.random() * 350)
        : Math.floor(Math.random() * 350);
      const intervalStep = Math.floor(Math.random() * (progress < statusBase.RPA_VIRAL ? 3 : 3));
      progress = Math.min(progress + intervalStep, maxDuringDownload);
      setLoadingPercent(progress);
      progressInterval = setTimeout(runProgressCycle, intervalDelay + randomDelayGivenStatus);
    };
    runProgressCycle();
  
    // Polling download status every 7s
    const pollingInterval = setInterval(async () => {
      try {
        if(progress>statusToMax.FILTERING){return}
        const downloadId = faldonId +
          + (filter?.formato_impresion ? ("::" + filter?.formato_impresion.toLowerCase()) : "") 
          + (filter?.f_negocio ? ("::" + filter?.f_negocio.toUpperCase()) : "") 
          + (safeProducts[0]?.nombreCampanaProd ? ("::" + safeProducts[0]?.nombreCampanaProd.toLowerCase()) : "");
        const statusResp = await getLatestDownloadFaldonStatus(downloadId, username);
        const newMax = statusToMax[statusResp.REQUEST_STATUS] ?? maxDuringDownload;
        if (newMax > maxDuringDownload) {
          maxDuringDownload = newMax;
          progress = statusBase[statusResp.REQUEST_STATUS];
          setLoadingPercent(progress);
        }
      } catch (err) {
        console.warn("status fetch failed.");
      }
    }, 7000);

    try{
      var faldones = [];
      if(selectedFaldones.length === 1){
        faldones.push(selectedFaldones[0]+"|"+cantidadCopias);
      } else {
        faldones = selectedFaldones.map(f => f+"|1");
      }
      const response = await downloadFaldonXlsxWithCopies(faldonId, {tienda, username, faldones});
      //const response = await downloadFaldonXlsx(faldonId, { tienda, username, products: selectedProductIds });

      clearInterval(progressInterval);
      clearInterval(pollingInterval);
      setLoadingPercent(97); // response arrived

      // 204 - (no elements for filtered faldon)
      if (response.status === 204) {
        toast({ icon: "info", title: "No hay eans que coincidan el faldón filtrado" });
        setLoadingPercent(100);
        setDownloading(false);
        return;
      }

      let downloadFilename = faldonId.replace(/\.(xlsx|xls)$/i, '.pdf');
      const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      if (disposition) {
        const match = disposition.match(/filename=\"?([^\";]+)\"?/);
        if (match && match[1]) {
          downloadFilename = match[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({ icon: "success", title: "Descarga iniciada" });
      setSelectedFaldones([]);
      setLoadingPercent(99);

    } catch (err) {
      clearInterval(progressInterval);
      clearInterval(pollingInterval);
      toast({ icon: "error", title: "Error al descargar" });
    }

    setLoadingPercent(100);
    setDownloading(false);
  };

  const columns = useMemo(
    () => [
      {
        id: "faldon",
        name: "Faldon",
        selector: (row) => row.faldon,
        cell: (row) => <div className="cell">{row.faldon.replace(/\.(xlsx|xls)$/i, '')}</div>,
        sortable: true,
      },
      {
        name: "Nombre Campaña",
        selector: (row) => row.nombreCampanaProd,
        cell: (row) => <div className="cell">{row.nombreCampanaProd || "N/A"}</div>,
        sortable: true,
      },
      {
        name: "Producto",
        selector: (row) => row.producto,
        cell: (row) => <div className="cell">{row.producto  || "N/A"}</div>,
        sortable: true,
      },
      {
        name: "Marca",
        selector: (row) => row.marca,
        cell: (row) => <div className="cell">{row.marca || "N/A"}</div>,
        sortable: true,
      },
      {
        name: "Categoria",
        selector: (row) => row.categoria,
        cell: (row) => <div className="cell">{row.categoria || "N/A"}</div>,
        sortable: true,
      },
      {
        name: "EAN 13",
        selector: (row) => row.ean13,
        cell: (row) => <div className="cell">{row.ean13 || "N/A"}</div>,
        sortable: true,
      },
      {
        name: "Porcentaje",
        selector: (row) => row.porcentaje,
        cell: (row) => <div className="cell">{row.porcentaje || "N/A"}</div>,
        sortable: true,
      },
      {
        name: "Precio Modificado",
        selector: (row) => row.precioModal,
        cell: (row) => <div className="cell">{row.precioModal || "N/A"}</div>,
        sortable: true,
      },
      {
        name: "Precio Oferta Unitario",
        selector: (row) => row.precioOfertaUnitario,
        cell: (row) => <div className="cell">{row.precioOfertaUnitario || "N/A"}</div>,
        sortable: true,
      },
      {
        name: "Seleccion",
        selector: (row) => row.id,
        cell: (row) => (
          <div className="cell">
            <input 
              type="checkbox" 
              checked={selectedFaldones.includes(row.faldon)}
              onChange={(e) => handleRowSelect(row, e.target.checked)}
            />
          </div>
        ),
        sortable: false
      }
    ],
    [selectedFaldones]
  );

  const [search, setSearch] = useState(filter?.ean ? filter.ean : "");

  // Reset search when filter changes (e.g., when toggling views)
  useEffect(() => {
    setSearch(filter?.ean ? filter.ean : "");
  }, [filter?.ean, toggleSwitch]);

  const filteredProducts = useMemo(() => {
    const _search = prepareText(search.trim());
    if (safeProducts.length === 0) return [];

    return safeProducts.filter((p) => {
      const _faldon = prepareText(p.faldon);
      const _producto = prepareText(p.producto);
      const _marca = prepareText(p.marca || "N/A");
      const _categoria = prepareText(p.categoria || "N/A");
      const _ean = prepareText(p.ean13 || "N/A");
      
      return (
        _faldon.includes(_search) ||
        _producto.includes(_search) ||
        _marca.includes(_search) ||
        _categoria.includes(_search) ||
        _ean.includes(_search)
      );
    });
  }, [search, safeProducts]);

  return (
    <Card className="smu-card">
      <Card.Header className="d-flex py-4">
        <div className="d-flex align-items-center me-auto">
          <SmuBadge color="#B1E5FC" />
          <span className="fs-5 ms-3 me-auto">ARTICULOS</span>
          <button
            className="smu-generic-button my-auto py-1 fs-6 px-3 d-flex align-items-center ms-4"
            onClick={() => {
              // Keep the preserve flag when navigating back to ProductPromotionsTable
              // The flag will be cleared when navigating away from the promotion flow
              navigate(-1);
            }}
          >
            <IconArrowLeft />
            <span className="ms-2">Atras</span>
          </button>
        </div>
        <div className="d-flex align-items-center">
          <SmuSearch
            value={search}
            onChange={({ target }) => setSearch(target.value)}
          />
        </div>
      </Card.Header>
      <div className="px-3 d-flex">
        <strong className="me-2" style={{ color: COLOR_PRIMARY }}>
          Id Faldon:
        </strong>
        {faldonId.replace(/\.(xlsx|xls)$/i, '')}
      </div>
      <div className="d-flex justify-content-between align-items-center gap-2 mt-3 px-3">
        <div className="d-flex align-items-center gap-2">
          <label htmlFor="custom-toggle-switch" className="mb-0" style={{ cursor: "pointer" }}>
            Visualización Nueva
          </label>
          <Form.Switch
            id="custom-toggle-switch"
            checked={toggleSwitch}
            onChange={(e) => onToggleSwitchChange && onToggleSwitchChange(e.target.checked)}
          />
        </div>
        <div className="d-flex gap-2 align-items-center">
          {selectedFaldones.length === 1 && (
            <div className="d-flex align-items-center">
              <label htmlFor="cantidadCopias" className="me-2 mb-0" style={{ fontWeight: 500 }}>
                Cantidad de copias:
              </label>
              <input
                id="cantidadCopias"
                type="number"
                min={1}
                max={100}
                step={1}
                value={typeof cantidadCopias !== "undefined" ? cantidadCopias : 1}
                onChange={e => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val > 0) {
                    const valueToSet = val > 100 ? 100 : val;
                    if (typeof setCantidadCopias === "function") {
                      setCantidadCopias(valueToSet);
                    }
                  }
                }}
                style={{ width: 60 }}
              />
            </div>
          )}
          <button
            className="btn btn-success btn-sm border-0"
            onClick={handleDownloadSelected}
            disabled={selectedFaldones.length === 0 || downloading}
          >
            {downloading ? `Descargando... ${loadingPercent}%` : `Descargar seleccionados (${selectedFaldones.length})`}
          </button>
          <button
            className="btn btn-secondary btn-sm border-0"
            onClick={handleSelectAll}
            disabled={downloading}
          >
            Marcar / Desmarcar
          </button>
        </div>
      </div>
      <Card.Body>
        <SmuTable
          columns={columns}
          data={filteredProducts}
          loading={loading}
          defaultSortFieldId="faldonXlsx"
          defaultSortAsc={false}
        />
      </Card.Body>
      {/* extended downloading indicator - split based in statusBase*/}
      {downloading && (
        <span
          className="text-muted small"
          style={{
            position: "fixed", borderRadius: "8px", padding: "8px 16px",
            bottom: 16, right: 24, zIndex: 9999,
            background: "rgba(255,255,255,0.95)", boxShadow: "0 2px 8px rgba(0,0,0,0.38)"
          }}
        >{loadingPercent <= 40 ? "1/2 Filtrando artículos de faldón..." : "2/2 Generando faldón PDF..."}</span>
      )}
    </Card>
  );
};

export default ProductsTable;
